import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("br", null, null, -1)

import BigNumber from 'bignumber.js';
import { computed, ComputedRef, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { TokenInfo } from '@/sdk/entities/portfolio';
import { EasyModeWithdrawForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { EasyModeWithdrawToken } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-token';
import { compareTokenAddresses, fromWei } from '@/sdk/utils';
import { BIG_ZERO, min } from '@/utils/bigNumber';
import { LOSS } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/LOSS';
import { USD_TOKEN } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/constants/USD_TOKEN';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';
import NoticeListWrapper from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/NoticeListWrapper.vue';
import DecreaseAmountNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/DecreaseAmountNotice.vue';
import SelectAnyTokensNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/SelectAnyTokensNotice.vue';
import SelectTokensNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/SelectTokensNotice.vue';
import UnstakeNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/UnstakeNotice.vue';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useEasyModeForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useStakedValue } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useStakedValue';


export default /*#__PURE__*/_defineComponent({
  __name: 'WithdrawNoticesList',
  setup(__props) {

const { t } = useI18n();
const { isPresentTokenIntoNetwork } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const {
  easyModeForm: easyModeWithdrawForm,
  lossType,
  getTokenInfoByFormToken,
  selectedTokens: withdrawSelectedTokens,
} = useEasyModeForm();
const easyModeForm = easyModeWithdrawForm as EasyModeWithdrawForm;
const selectedTokens = withdrawSelectedTokens as ComputedRef<EasyModeWithdrawToken[]>;

const translationPath = 'portfolio.easyModeTranslations.withdraw.noticesList';

/* template data */
const color = ref<'info' | 'warning'>('warning');

const mainHeader = ref('');
const mainTooltip = ref('');
const applyHeader = ref('');

const amountToUnstake = ref<BigNumber | null>(null);
const tokensToSelect = ref<TokenInfo[] | null>(null);
const showSelectAnyTokensNotice = ref<boolean>(false);
const decreaseAmount = ref<{ toAmount: BigNumber; symbol: string } | null>(null);

const showUnstakeNotice = computed(() => amountToUnstake.value && amountToUnstake.value.gt(0));
const showSelectTokensNotice = computed(
  () => !!tokensToSelect.value && tokensToSelect.value.length > 0,
);
const showDecreaseAmountNotice = computed(() => !!decreaseAmount.value);
const emptyApplyBody = computed(
  () =>
    applyHeader.value &&
    !(
      showUnstakeNotice.value ||
      showSelectTokensNotice.value ||
      showSelectAnyTokensNotice.value ||
      showDecreaseAmountNotice.value
    ),
);

const shouldShowBlock = computed(
  () => easyModeForm.lpTokenAmount.gt(0) && mainHeader.value && !emptyApplyBody.value,
);

// to keep template typing working
const _amountToUnstake = computed(() => amountToUnstake.value as BigNumber);
const _tokensToSelect = computed(() => tokensToSelect.value as TokenInfo[]);
const _decreaseAmount = computed(
  () => decreaseAmount.value as { toAmount: BigNumber; symbol: string },
);

/* computed properties for imperative calculation */
const lpTokenBalanceInCardano = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) {
    return BIG_ZERO;
  }

  const { symbol } = easyModeForm.lpToken;

  if (!isPresentTokenIntoNetwork(symbol!, +DEFAULT_CARDANO_CHAIN_ID!)) {
    return BIG_ZERO;
  }

  const rawBalance = balanceByTokenSymbolAndChainId(symbol!, +DEFAULT_CARDANO_CHAIN_ID!).value
    ?.balance;
  return BigNumber(rawBalance?.toExact() || BIG_ZERO);
});
const lpTokenBalanceInMilkomeda = computed(() => easyModeForm.portfolio.getBalanceOfLp());
const lpTokenBalance = computed(() =>
  lpTokenBalanceInMilkomeda.value.plus(lpTokenBalanceInCardano.value),
);
const stakedAmount = useStakedValue();

// Sum amounts of selected tokens in LP.
const selectedSuppliesSum = computed(() =>
  selectedTokens.value.reduce((acc, token) => {
    const tokenInfo = getTokenInfoByFormToken(token);

    const supplyEquivalentInBaseTokenWei = tokenInfo.baseTokenAmountEquivalent;
    // In BASE WEI / LP WEI
    // NOTE: Changed `LP_TOKEN_DECIMALS` -> easyModeForm.lpToken.decimals
    const lpTokenPriceBaseInWei = easyModeForm.portfolio.lpTokenPriceBase.shiftedBy(
      easyModeForm.portfolio.baseToken.decimals - easyModeForm.lpToken.decimals,
    );
    // BASE WEI / ( BASE WEI / LP WEI ) => LP WEI
    const supplyEquivalentInLpTokenWei = supplyEquivalentInBaseTokenWei.div(lpTokenPriceBaseInWei);

    // NOTE: Changed `LP_TOKEN_DECIMALS` -> easyModeForm.lpToken.decimals
    return acc.plus(fromWei(supplyEquivalentInLpTokenWei, easyModeForm.lpToken.decimals));
  }, BIG_ZERO),
);

const totalGTLpBalanceAndStaked = computed(() =>
  easyModeForm.lpTokenAmount.gt(lpTokenBalance.value.plus(stakedAmount.value)),
);
const totalGTLpBalance = computed(() =>
  easyModeForm.lpTokenAmount.gt(
    lpTokenBalance.value.plus(easyModeWithdrawForm.useFarmsLp ? stakedAmount.value : 0),
  ),
);
const noTokensSelected = computed(() => easyModeForm.tokens.every(token => !token.checked));

const totalGTSelectedSuppliesSum = computed(() =>
  easyModeForm.lpTokenAmount.gt(selectedSuppliesSum.value),
);
const feeCallReverted = computed(() => easyModeForm.impactError);

const resetRefs = () => {
  color.value = 'warning';

  mainHeader.value = '';
  mainTooltip.value = '';
  applyHeader.value = '';

  amountToUnstake.value = null;
  tokensToSelect.value = null;
  showSelectAnyTokensNotice.value = false;
  decreaseAmount.value = null;
};

watchEffect(() => {
  resetRefs();

  if (totalGTLpBalanceAndStaked.value) {
    setWarningColor();
    setHeader('balanceIsNotEnough');
    setSimpleApplyHeader();

    setUnstakeNotice();
    loggingSetDecreaseAmount('amount withdraw > (user LP balance + staked LP)');
    setDecreaseAmountToByLPTokenValue(lpTokenBalance.value.plus(stakedAmount.value));
    return;
  }

  if (totalGTLpBalance.value) {
    setWarningColor();
    setHeader('balanceIsNotEnough');
    setSimpleApplyHeader();

    setUnstakeNotice();
    loggingSetDecreaseAmount('amount withdraw > user LP balance');
    setDecreaseAmountToByLPTokenValue(lpTokenBalance.value);
    return;
  }

  if (noTokensSelected.value) {
    setInfoColor();
    setHeader('noSelectedTokens');
    setEmptyApplyHeader();
    return;
  }

  if (totalGTSelectedSuppliesSum.value) {
    setWarningColor();
    setHeader('supplyOfSelectedIsNotEnough');
    setSimpleApplyHeader();

    showSelectAnyTokensNotice.value = true;
    loggingSetDecreaseAmount('amount withdraw > sum amounts of selected tokens');
    setDecreaseAmountToByLPTokenValue(selectedSuppliesSum.value);
    return;
  }

  if (feeCallReverted.value) {
    setWarningColor();
    setHeaderAndTooltip('callReverted');
    setMinimiseLossApplyHeader();

    setTokensToSelect();
    loggingSetDecreaseAmountToDistributedAfterFirstIteration('Impact error (callReverted)');
    setDecreaseToNotDistributedAmount();
    return;
  }

  if (lossType.value === LOSS.SMALL) {
    setInfoColor();
    setHeaderAndTooltip('smallLoss');
    setMinimiseLossApplyHeader();

    setTokensToSelect();
    loggingSetDecreaseAmountToDistributedAfterFirstIteration('SMALL loss');
    setDecreaseToNotDistributedAmount();
    return;
  }

  if (lossType.value === LOSS.HUGE) {
    setWarningColor();
    setHeaderAndTooltip('hugeLoss');
    setMinimiseLossApplyHeader();

    setTokensToSelect();
    loggingSetDecreaseAmountToDistributedAfterFirstIteration('HUGE loss');
    setDecreaseToNotDistributedAmount();
    return;
  }
});

function setUnstakeNotice(): void {
  const valueToUnstake = min(
    easyModeForm.lpTokenAmount.minus(lpTokenBalance.value),
    stakedAmount.value,
  );
  amountToUnstake.value = valueToUnstake;
}

function setTokensToSelect(): void {
  const formTokensToSelect = easyModeForm.tokens.filter(
    token => !token.checked && !token.disabled && token.noLossLimit.gt(0),
  );
  tokensToSelect.value = formTokensToSelect.map(getTokenInfoByFormToken);
}

function setDecreaseAmountToByLPTokenValue(lpTokenValue: BigNumber): void {
  const symbol = easyModeForm.selectedCurrencySymbol;
  let toAmount = lpTokenValue;

  if (symbol === USD_TOKEN.symbol) {
    // (USD / LP) * LP => USD
    toAmount = easyModeForm.portfolio.getLpTokenPriceUSD().multipliedBy(lpTokenValue);
  }

  decreaseAmount.value = { symbol, toAmount };
}

function setDecreaseToNotDistributedAmount(): void {
  const distributedAfterFirstIterationAmount = easyModeForm.lpTokenAmount.minus(
    easyModeForm.restAmountToDistributeAfterFirstIteration,
  );

  if (
    !easyModeForm.restAmountToDistributeAfterFirstIteration.isZero() &&
    distributedAfterFirstIterationAmount.gt(0)
  ) {
    setDecreaseAmountToByLPTokenValue(distributedAfterFirstIterationAmount);
  }
}

function setHeader(name: string) {
  mainHeader.value = t(`${translationPath}.headers.${name}`);
}

function setHeaderAndTooltip(name: string) {
  mainHeader.value = t(`${translationPath}.headers.${name}.text`);
  mainTooltip.value = t(`${translationPath}.headers.${name}.tooltip`);
}

function setSimpleApplyHeader() {
  applyHeader.value = t(`${translationPath}.headers.applyChanges`);
}

function setMinimiseLossApplyHeader() {
  applyHeader.value = t(`${translationPath}.headers.applyChangesToMinimise`);
}

function setEmptyApplyHeader() {
  applyHeader.value = '';
}

function setWarningColor() {
  color.value = 'warning';
}

function setInfoColor() {
  color.value = 'info';
}

const onSelectToken = (tokenInfo: TokenInfo): void => {
  const formToken = easyModeForm.tokens.find(token =>
    compareTokenAddresses(tokenInfo.token.address, token.address),
  )!;

  formToken.checked = true;
};

const onDecreaseAmount = ({ toAmount, symbol }: { toAmount: BigNumber; symbol: string }): void => {
  let newLpTokenAmount = toAmount;

  if (symbol === USD_TOKEN.symbol) {
    newLpTokenAmount = toAmount.div(easyModeForm.portfolio.getLpTokenPriceUSD());
  }

  easyModeForm.lpTokenAmount = newLpTokenAmount;
};

// DEBUG

function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].EASY_MODE_WITHDRAW;
}

function loggingSetDecreaseAmount(cause: string) {
  if (isLoggingDisabled()) return;

  console.log(`[WITHDRAW] ${cause} : set decrease.`);
}

function loggingSetDecreaseAmountToDistributedAfterFirstIteration(cause: string) {
  if (isLoggingDisabled()) return;

  console.log(`[WITHDRAW] ${cause} : set decrease : distributed after first iteration.`);
}

return (_ctx: any,_cache: any) => {
  return (_unref(shouldShowBlock))
    ? (_openBlock(), _createBlock(NoticeListWrapper, {
        key: 0,
        color: color.value
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", null, [
            _createTextVNode(_toDisplayString(mainHeader.value) + " ", 1),
            (mainTooltip.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(TooltipIcon, {
                    tooltip: mainTooltip.value,
                    color: color.value,
                    "no-space": "",
                    class: "ml-s1",
                    inline: ""
                  }, null, 8, ["tooltip", "color"]),
                  _hoisted_1
                ], 64))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(applyHeader.value), 1)
          ]),
          (_unref(showUnstakeNotice))
            ? (_openBlock(), _createBlock(UnstakeNotice, {
                key: 0,
                amount: _unref(_amountToUnstake)
              }, null, 8, ["amount"]))
            : _createCommentVNode("", true),
          (_unref(showSelectTokensNotice))
            ? (_openBlock(), _createBlock(SelectTokensNotice, {
                key: 1,
                "tokens-to-select": _unref(_tokensToSelect),
                onSelectToken: onSelectToken
              }, null, 8, ["tokens-to-select"]))
            : _createCommentVNode("", true),
          (showSelectAnyTokensNotice.value)
            ? (_openBlock(), _createBlock(SelectAnyTokensNotice, { key: 2 }))
            : _createCommentVNode("", true),
          (_unref(showDecreaseAmountNotice))
            ? (_openBlock(), _createBlock(DecreaseAmountNotice, {
                key: 3,
                "to-amount": _unref(_decreaseAmount).toAmount,
                symbol: _unref(_decreaseAmount).symbol,
                onClick: onDecreaseAmount
              }, null, 8, ["to-amount", "symbol"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["color"]))
    : _createCommentVNode("", true)
}
}

})